import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Accessibility, Mail, Phone } from "lucide-react";
import SEO from "@/components/SEO";

export const AccessibilityPage = () => {
  return (
    <div className="pt-20" data-testid="accessibility-page">
      <SEO
        title="Accessibility Statement | Coastal Collaborative"
        description="Coastal Collaborative is committed to WCAG 2.1 Level AA accessibility. Read our accessibility statement and contact us if you encounter any barriers."
        canonical="https://www.workcoastal.com/accessibility"
      />
      {/* Hero Section */}
      <section className="py-16 bg-brand" data-testid="accessibility-hero">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Accessibility className="w-16 h-16 text-sea mx-auto mb-6" strokeWidth={1.5} />
            <h1 className="font-serif text-4xl sm:text-5xl font-bold text-white leading-tight mb-4">
              Website Accessibility Statement
            </h1>
            <p className="font-sans text-white/70">
              Last Updated: February 27, 2026
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white" data-testid="accessibility-content">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="prose prose-lg max-w-none"
          >
            <div className="space-y-10">
              {/* Our Commitment */}
              <div>
                <h2 className="font-serif text-2xl font-bold text-brand mb-4">
                  Our Commitment to Accessibility
                </h2>
                <p className="font-sans text-muted-foreground leading-relaxed">
                  We are committed to ensuring digital accessibility for people with disabilities. 
                  We are continually improving the user experience for everyone and applying relevant 
                  accessibility standards to enhance usability and inclusivity. Our goal is to provide 
                  a website that is accessible to the widest possible audience, regardless of technology or ability.
                </p>
              </div>

              {/* Accessibility Standards */}
              <div>
                <h2 className="font-serif text-2xl font-bold text-brand mb-4">
                  Accessibility Standards
                </h2>
                <p className="font-sans text-muted-foreground leading-relaxed">
                  We strive to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA, 
                  which are widely recognized as the industry standard for website accessibility. 
                  Accessibility is an ongoing process, and we are actively working toward improving 
                  the accessibility and usability of our website.
                </p>
              </div>

              {/* Technology & Tools */}
              <div>
                <h2 className="font-serif text-2xl font-bold text-brand mb-4">
                  Accessibility Technology & Tools
                </h2>
                <p className="font-sans text-muted-foreground leading-relaxed mb-4">
                  To support our accessibility efforts, we have deployed industry-standard accessibility 
                  technology designed to enhance usability and assist visitors with disabilities. 
                  These tools may provide features such as:
                </p>
                <ul className="space-y-2 font-sans text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-sea">•</span>
                    Screen reader enhancements
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-sea">•</span>
                    Keyboard navigation improvements
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-sea">•</span>
                    Color contrast adjustments
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-sea">•</span>
                    Text size and spacing controls
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-sea">•</span>
                    Focus indicators
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-sea">•</span>
                    Additional accessibility interface options
                  </li>
                </ul>
                <p className="font-sans text-muted-foreground leading-relaxed mt-4">
                  This technology helps us improve accessibility while we continue broader auditing, 
                  remediation, and development efforts.
                </p>
              </div>

              {/* Ongoing Improvement */}
              <div>
                <h2 className="font-serif text-2xl font-bold text-brand mb-4">
                  Ongoing Improvement
                </h2>
                <p className="font-sans text-muted-foreground leading-relaxed mb-4">
                  Accessibility is an ongoing initiative. We are:
                </p>
                <ul className="space-y-2 font-sans text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-sea">•</span>
                    Reviewing site content and functionality regularly
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-sea">•</span>
                    Working with developers and accessibility tools
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-sea">•</span>
                    Identifying and remediating barriers
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-sea">•</span>
                    Incorporating accessibility into future design and development
                  </li>
                </ul>
                <p className="font-sans text-muted-foreground leading-relaxed mt-4">
                  We understand accessibility is a continuous journey and are committed to making improvements over time.
                </p>
              </div>

              {/* Third-Party Content */}
              <div>
                <h2 className="font-serif text-2xl font-bold text-brand mb-4">
                  Third-Party Content
                </h2>
                <p className="font-sans text-muted-foreground leading-relaxed">
                  Some areas of the website may include third-party content, plugins, or integrations 
                  that are not fully controlled by us. While we encourage vendors to provide accessible 
                  content, we cannot guarantee accessibility of third-party features. However, we welcome 
                  feedback regarding any issues encountered.
                </p>
              </div>

              {/* Feedback & Assistance */}
              <div className="bg-sand-light/30 rounded-2xl p-8">
                <h2 className="font-serif text-2xl font-bold text-brand mb-4">
                  Feedback & Assistance
                </h2>
                <p className="font-sans text-muted-foreground leading-relaxed mb-4">
                  If you experience difficulty accessing any part of this website, need assistance, 
                  or would like to request content in an alternative format, please contact us. Please include:
                </p>
                <ul className="space-y-2 font-sans text-muted-foreground mb-6">
                  <li className="flex items-start gap-2">
                    <span className="text-sea">•</span>
                    The page URL
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-sea">•</span>
                    Description of the issue
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-sea">•</span>
                    Your preferred contact method
                  </li>
                </ul>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link 
                    to="/contact"
                    className="inline-flex items-center gap-2 font-sans text-sm font-medium text-sea hover:text-sea-light transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    Contact Us Page
                  </Link>
                  <a 
                    href="tel:321-866-8572"
                    className="inline-flex items-center gap-2 font-sans text-sm font-medium text-sea hover:text-sea-light transition-colors"
                  >
                    <Phone className="w-4 h-4" />
                    (321) 866-8572
                  </a>
                </div>
                <p className="font-sans text-muted-foreground leading-relaxed mt-4">
                  We aim to respond within a reasonable timeframe and will make reasonable efforts 
                  to provide accessible alternatives.
                </p>
              </div>

              {/* Good Faith Efforts */}
              <div>
                <h2 className="font-serif text-2xl font-bold text-brand mb-4">
                  Good Faith Efforts
                </h2>
                <p className="font-sans text-muted-foreground leading-relaxed">
                  We are making good-faith efforts to comply with accessibility standards and improve 
                  usability for individuals with disabilities. Despite our efforts, some content may 
                  not yet fully meet the strictest accessibility standards due to the evolving nature 
                  of technology and ongoing updates. We appreciate your understanding as we continue 
                  improving accessibility.
                </p>
              </div>

              {/* Terms & Conditions */}
              <div>
                <h2 className="font-serif text-2xl font-bold text-brand mb-4">
                  Terms & Conditions — Accessibility Clause
                </h2>
                <p className="font-sans text-muted-foreground leading-relaxed">
                  We are committed to providing an accessible website experience for all users and 
                  strive to follow recognized accessibility standards, including WCAG guidelines. 
                  We have implemented industry-standard accessibility tools and continue to review, 
                  update, and improve our website to enhance accessibility. Because accessibility is 
                  an ongoing process, some areas of the website may not yet fully conform to all 
                  accessibility standards. If you encounter accessibility barriers, please contact us 
                  so we can provide assistance, offer alternative access, and address the issue where 
                  feasible. Nothing in these Terms is intended to limit any rights available under 
                  applicable accessibility laws.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AccessibilityPage;
